import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { useGetProgramsQuery } from 'services/cms';
import { CmsCard } from 'types';
import { filterProgramsByAgeAndLiveType } from 'utils/ageRating';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';
import { useDebounce } from 'utils/useDebounce';

import Section from 'components/Section';

type SearchVerticalListProps = {
  searchInputValue?: string;
  categoryValue?: ProgramType;
  containerWidth?: number | undefined;
  topContainerHeight?: number | undefined;
};

const SearchVerticalList: React.FC<SearchVerticalListProps> = ({
  categoryValue,
  searchInputValue,
  ...props
}) => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const analytics = useAnalytics();

  const delayedValue = useDebounce(searchInputValue);
  const { selectedProfile } = useSelectedProfile();

  const { data, isLoading, isFetching, isSuccess } = useGetProgramsQuery({
    q: delayedValue,
    type: categoryValue,
    page,
  });

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMorePages(true);
    if (delayedValue) analytics.triggerSearchEvent(delayedValue);
  }, [delayedValue, categoryValue]);

  useEffect(() => {
    if (isFetching || !isSuccess || !data?.items) {
      return;
    }

    const filteredData = filterProgramsByAgeAndLiveType(data, selectedProfile?.ageRating ?? '18');

    if (filteredData?.items) {
      if (page === 1) {
        setItems([...filteredData.items]);
      } else {
        setItems((oldItems) => {
          const newItems = filteredData.items.filter(
            (newItem: any) => !oldItems.find((oldItem) => oldItem._id === newItem._id)
          );
          return [...oldItems, ...newItems];
        });
      }

      const lastFetchItemCount = filteredData?.items?.length;
      const limitPerPage = filteredData?.meta?.limit || (Platform.isTV ? 8 : 10);
      setHasMorePages(lastFetchItemCount >= limitPerPage);
    } else {
      if (page === 1) {
        setItems([]);
      }
      setHasMorePages(false);
    }
  }, [data, isFetching, isSuccess, selectedProfile?.ageRating]);

  const formattedData: CmsCard[] = useMemo(() => {
    return (
      items.map<CmsCard>((item) => ({
        type: item.type,
        title: item.title,
        image: item.imageVertical || item.image,
        link: `/${item.url}`,
        height: item.imageVertical?.height || item.image?.height,
        _id: item._id,
        id: item.id,
        search_term: delayedValue,
        search_category: categoryValue,
      })) || []
    );
  }, [items, delayedValue, categoryValue]);

  const HandleOnEndReached = useCallback(() => {
    if (!isFetching && hasMorePages) {
      setPage((p) => p + 1);
    }
  }, [isFetching, hasMorePages]);

  const showInitialLoading = isLoading && page === 1;
  const showPaginationLoading = isFetching && page > 1;

  if (showInitialLoading) {
    return (
      <View style={styles.showInitialLoadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Section
        onEndReached={HandleOnEndReached}
        vertical
        items={formattedData}
        _name="section"
        showTitle={false}
        loading={isLoading}
        variant="section.medium"
        title={searchInputValue || ''}
        {...props}
      />
      {showPaginationLoading && (
        <View style={styles.spinnerContainer}>
          <ActivityIndicator size="small" color="#0000ff" />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    flex: 1,
  },
  spinnerContainer: {
    position: 'absolute',
    width: '100%',
    top: 0,
    bottom: calcSizeForDevice(20),
    justifyContent: 'center',
    alignItems: 'center',
  },
  showInitialLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default SearchVerticalList;
